export const useSwimmersHandlers = ({
  swimmersList,
  setSwimmersList,
  setSwimmers,
  setSwimmersData,
  setCurrentSwimmerId,
  currentSwimmerId,
  isRunning,
  newSwimmer,
  setNewSwimmer,
  deleteConfirmation,
  setDeleteConfirmation,
  swimmersData,
  updateResult,
  poolLength,
  raceDistance,
  showSuccess,
  showError
}) => {
  const handleAddSwimmer = () => {
    try {
      if (newSwimmer.trim()) {
        const updatedSwimmers = [...swimmersList, newSwimmer.trim()];
        setSwimmersList(updatedSwimmers);
        setSwimmers(updatedSwimmers);
        
        const updatedData = {
          ...swimmersData,
          [newSwimmer.trim()]: {
            currentLapTime: 0,
            laps: [],
            totalDistance: 0,
            average: 0,
            poolLength: poolLength,
            raceDistance: raceDistance,
            lastUpdate: new Date().toISOString()
          }
        };
        
        setSwimmersData(updatedData);
        localStorage.setItem('swimmersData', JSON.stringify(updatedData));
        localStorage.setItem('swimmersList', JSON.stringify(updatedSwimmers));
        
        setNewSwimmer('');
        showSuccess('Nadador adicionado com sucesso!');
      }
    } catch (error) {
      showError('Erro ao adicionar nadador');
      console.error('Erro ao adicionar nadador:', error);
    }
  };

  const handleRemoveSwimmer = (swimmerToRemove) => {
    try {
      if (swimmersList.length <= 1) {
        showError('Não é possível remover o último nadador');
        return;
      }
      
      const updatedSwimmers = swimmersList.filter(s => s !== swimmerToRemove);
      setSwimmersList(updatedSwimmers);
      setSwimmers(updatedSwimmers);
      
      const updatedData = { ...swimmersData };
      delete updatedData[swimmerToRemove];
      
      setSwimmersData(updatedData);
      localStorage.setItem('swimmersData', JSON.stringify(updatedData));
      localStorage.setItem('swimmersList', JSON.stringify(updatedSwimmers));
      
      if (swimmerToRemove === currentSwimmerId) {
        const newCurrentSwimmer = updatedSwimmers[0];
        setCurrentSwimmerId(newCurrentSwimmer);
        localStorage.setItem('currentSwimmerId', newCurrentSwimmer);
      }
      showSuccess('Nadador removido com sucesso!');
    } catch (error) {
      showError('Erro ao remover nadador');
      console.error('Erro ao remover nadador:', error);
    }
  };

  const handleDragEnd = (event) => {
    try {
      const { active, over } = event;
      
      if (!over || (isRunning && active.id === currentSwimmerId)) {
        return;
      }
      
      if (active.id !== over.id) {
        const oldIndex = swimmersList.indexOf(active.id);
        const newIndex = swimmersList.indexOf(over.id);
        const newOrder = arrayMove(swimmersList, oldIndex, newIndex);
        
        setSwimmersList(newOrder);
        setSwimmers(newOrder);
        localStorage.setItem('swimmersList', JSON.stringify(newOrder));
        showSuccess('Ordem dos nadadores atualizada!');
      }
    } catch (error) {
      showError('Erro ao reordenar nadadores');
      console.error('Erro ao reordenar:', error);
    }
  };

  const handleSwimmerDoubleClick = (swimmer) => {
    try {
      if (!isRunning) {
        setCurrentSwimmerId(swimmer);
        localStorage.setItem('currentSwimmerId', swimmer);
        showSuccess(`${swimmer} selecionado como nadador atual`);
      }
    } catch (error) {
      showError('Erro ao selecionar nadador');
      console.error('Erro ao selecionar nadador:', error);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.swimmer) {
      handleRemoveSwimmer(deleteConfirmation.swimmer);
    }
    setDeleteConfirmation({
      open: false,
      swimmer: null
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      open: false,
      swimmer: null
    });
  };

  const handleEditSwimmer = (editData) => {
    if (!editData || typeof editData !== 'object') {
      showError('Dados de edição inválidos');
      console.error('Dados de edição inválidos:', editData);
      return false;
    }

    const { oldName, newName, laps } = editData;

    if (!oldName || !newName || !Array.isArray(laps)) {
      showError('Dados de edição incompletos');
      console.error('Dados de edição incompletos');
      return false;
    }

    try {
      const updatedSwimmers = swimmersList.map(name => 
        name === oldName ? newName : name
      );

      const updatedData = { ...swimmersData };
      const oldSwimmerData = updatedData[oldName] || {};
      const distance = raceDistance || poolLength;
      
      const processedLaps = laps.map(lap => ({
        time: typeof lap === 'object' ? lap.time : lap,
        edited: true,
        timestamp: new Date().toISOString()
      }));

      updatedData[newName] = {
        ...oldSwimmerData,
        currentLapTime: processedLaps[processedLaps.length - 1]?.time || 0,
        laps: processedLaps,
        totalDistance: processedLaps.length * distance,
        average: processedLaps.length > 0 
          ? processedLaps.reduce((a, b) => a + b.time, 0) / processedLaps.length 
          : 0,
        poolLength: poolLength,
        raceDistance: raceDistance,
        lastUpdate: new Date().toISOString()
      };

      if (oldName !== newName) {
        delete updatedData[oldName];
      }

      setSwimmersList(updatedSwimmers);
      setSwimmers(updatedSwimmers);
      setSwimmersData(updatedData);

      updateResult({
        oldName,
        newName,
        laps: processedLaps.map(lap => lap.time),
        poolLength,
        raceDistance
      });

      localStorage.setItem('swimmersList', JSON.stringify(updatedSwimmers));
      localStorage.setItem('swimmersData', JSON.stringify(updatedData));
      localStorage.setItem('results', JSON.stringify(updatedData));

      if (oldName === currentSwimmerId) {
        setCurrentSwimmerId(newName);
        localStorage.setItem('currentSwimmerId', newName);
      }

      showSuccess('Dados do nadador atualizados com sucesso!');
      return true;
    } catch (error) {
      showError('Erro ao atualizar dados do nadador');
      console.error('Erro ao processar edição:', error);
      return false;
    }
  };

  return {
    handleAddSwimmer,
    handleRemoveSwimmer,
    handleDragEnd,
    handleSwimmerDoubleClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleEditSwimmer
  };
};

const arrayMove = (array, from, to) => {
  const newArray = array.slice();
  newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0]);
  return newArray;
};